<script setup lang="ts">
import { computed, onMounted, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useColorMode } from '@vueuse/core'
import { useAuthStore } from '~/stores/authStore'
import { useCentralConfigStore } from '~/stores/centralConfigStore'
import { useMenuVisibility } from '~/composables/useMenuVisibility'
import ModeSwitcher from '~/components/ModeSwitcher.vue'
import LanguageSwitcher from '~/components/LanguageSwitcher.vue'
import { SKIP_NAME_INFO } from '~/src/constants'
import { SkipNameInfo } from '~/src/types'
import { useLogger } from '~/composables/useLogger'


const colorMode = useColorMode()
const { t } = useI18n()
const { showAudio, showRadio } = useMenuVisibility()
const logger = useLogger()

const authStore = useAuthStore()
const { isAdmin, helperForMultipleUsers, helperPerson, userPerson } = storeToRefs(authStore)
const centralConfigStore = useCentralConfigStore()
const { isHideExtendedMenu } = storeToRefs(centralConfigStore)

const router = useRouter()

// Ensure central config is initialized
onBeforeMount(async () => {
  logger.debug('Default layout: Ensuring central config is initialized')
  await centralConfigStore.ensureInitialized()
})

onMounted(() => {
  colorMode.value = 'light'
})

const logout = () => {
  authStore.logout()
  navigateTo('/login')
}

const switchUser = async () => {
  await authStore.switchUser()
  await router.replace({ path: '/', force: true })
}

const baseLinks = computed(() => {
  const links = [
    {
      id: 'home',
      label: t('navigation.sections.home.label'),
      icon: 'i-heroicons-home',
      to: '/',
      tooltip: {
        text: t('navigation.sections.home.tooltip'),
        shortcuts: ['G', 'H']
      }
    },
    {
      id: 'calendar',
      label: t('navigation.sections.calendar.label'),
      icon: 'i-heroicons-calendar',
      to: '/calendar',
      tooltip: {
        text: t('navigation.sections.calendar.tooltip'),
        shortcuts: ['C']
      }
    },
  ]

  const linksExtended = [
    {
      id: 'albums',
      label: t('navigation.sections.albums.label'),
      icon: 'i-heroicons-photo',
      to: '/albums',
      tooltip: {
        text: t('navigation.sections.albums.tooltip'),
        shortcuts: ['A']
      }
    },
    {
      id: 'mini-sudoku',
      label: t('navigation.sections.mini_sudoku.label'),
      icon: 'mdi:grid',
      to: '/minisudoku/minisudokusettings',
      tooltip: {
        text: t('navigation.sections.mini_sudoku.tooltip'),
        shortcuts: ['S']
      }
    },
    {
      id: 'solitaire',
      label: t('navigation.sections.solitaire.label'),
      icon: 'mdi:cards-playing-heart-multiple-outline',
      to: '/solitaire/solitairesettings',
      tooltip: {
        text: t('navigation.sections.solitaire.tooltip'),
        shortcuts: ['K']
      }
    },
    {
      id: 'jigsaw',
      label: t('navigation.sections.jigsaw.label'),
      icon: 'i-heroicons-puzzle-piece',
      to: '/jigsaw',
      tooltip: {
        text: t('navigation.sections.jigsaw.tooltip'),
        shortcuts: ['J']
      }
    }
  ]

  if (!isHideExtendedMenu.value) {
    // push all the extended links
    links.push(...linksExtended)
  }

  if (showAudio.value) {
    links.push({
      id: 'audio',
      label: t('navigation.sections.audio.label'),
      icon: 'i-heroicons-speaker-wave',
      to: '/audio/audiosettings',
      tooltip: {
        text: t('navigation.sections.audio.tooltip'),
        shortcuts: ['A']
      }
    })
  }

  if (showRadio.value) {
    links.push({
      id: 'radio',
      label: t('navigation.sections.radio.label'),
      icon: 'mdi:radio',
      to: '/radio/radiosettings',
      tooltip: {
        text: t('navigation.sections.radio.tooltip'),
        shortcuts: ['R']
      }
    })
  }

  return links
})

const adminLinks: any[] = []

if (isAdmin.value) {
  adminLinks.push(
    {
      id: 'admin',
      label: 'Admin: Users',
      icon: 'i-heroicons-user-group',
      to: '/admin/users',
      tooltip: {
        text: 'Admin',
        shortcuts: ['A']
      }
    },
    {
      id: 'admin',
      label: 'Admin: Knowledge Graph',
      icon: 'i-heroicons-user-group',
      to: '/admin/test-knowledge-graph',
      tooltip: {
        text: 'Admin',
        shortcuts: ['K']
      }
    },
    // {
    //   id: 'admin',
    //   label: 'Admin: Error',
    //   icon: 'i-heroicons-exclamation-triangle',
    //   to: '/test-error-logging',
    //   tooltip: {
    //     text: 'Test Error Logging',
    //     shortcuts: ['E']
    //   }
    // }
  )
}

const helperLinks = computed(() => {
  const links = []

  if (!(SKIP_NAME_INFO === SkipNameInfo.Skip)) {
    links.push({
      id: 'helpers',
      label: t('pages.helpers.title'),
      icon: 'i-heroicons-user-group',
      to: '/helpers',
      tooltip: {
        text: t('pages.helpers.tooltip'),
        shortcuts: ['I']
      }
    })
  }

  links.push({
    id: 'helper-qr-registration',
    label: t('pages.helperUserDeviceRegistration.title'),
    icon: 'i-heroicons-device-tablet',
    to: '/senior-device-registration',
    tooltip: {
      text: t('pages.helperUserDeviceRegistration.tooltip'),
      shortcuts: ['S']
    }
  })

  return links
})

interface FooterLink {
  id?: string
  label: string
  icon: string
  to?: string
  click?: () => void
  tooltip?: {
    text: string
    shortcuts: string[]
  }
}

const footerLinks = computed<FooterLink[]>(() => {
  const links: FooterLink[] = []


  if (!(SKIP_NAME_INFO === SkipNameInfo.Skip)) {
    links.push({
      label: userPerson.value ? `${t('navigation.user')}: ${userPerson.value.firstname} ${userPerson.value.lastname}` : '',
      icon: 'i-heroicons-user',
      to: '#'
    })

    links.push({
      label: helperPerson.value ? `${t('navigation.helper')}: ${helperPerson.value.firstname} ${helperPerson.value.lastname}` : '',
      icon: 'i-heroicons-user-circle',
      to: '#'
    })
  }

  links.push({
    label: t('navigation.footer.version'),
    icon: 'i-heroicons-information-circle',
    to: '/version'
  })

  // Add switch user link if helper has multiple users
  if (helperForMultipleUsers.value) {
    links.push({
      id: 'switch-user',
      label: t('navigation.switch_user.label'),
      icon: 'i-heroicons-arrows-right-left',
      click: switchUser,
      tooltip: {
        text: t('navigation.switch_user.tooltip'),
        shortcuts: ['U']
      }
    })
  }

  // Add delete account link
  links.push({
    label: t('pages.delete_account.title'),
    icon: 'i-heroicons-trash',
    to: '/delete-account'
  })

  // Add logout link last
  links.push({
    label: t('navigation.footer.logout'),
    icon: 'i-heroicons-arrow-left-start-on-rectangle',
    click: logout
  })

  return links
})

onMounted(async () => {
})
</script>

<template>
  <div>
    <ModeSwitcher />
    <VitePwaManifest />

    <UDashboardLayout class="safe-area-top helper-dashboard">
      <UDashboardPanel :width="250" :resizable="{ min: 200, max: 300 }" collapsible class="safe-area-top" :ui="{
        wrapper: 'flex-col items-stretch relative w-full safe-area-top lg:w-[--width] flex-shrink-0 hidden lg:flex'
      }">
        <UDashboardNavbar class="safe-area-top" :ui="{
          wrapper: 'h-[--header-height] flex-shrink-0 flex items-center !border-0 !border-none px-4 gap-x-4 min-w-0'
        }">
          <template #title>
            <div>{{ t('navigation.sections.home.label') }}</div>
          </template>
        </UDashboardNavbar>

        <UDashboardSidebar class="safe-area-top">
          <UDashboardSidebarLinks :links="baseLinks" />

          <UDivider v-if="helperLinks.length > 0" />
          <UDashboardSidebarLinks v-if="helperLinks.length > 0" :links="helperLinks" />

          <UDivider v-if="isAdmin" />
          <UDashboardSidebarLinks v-if="isAdmin" :links="adminLinks" />

          <div class="flex-1" />
          <UDashboardSidebarLinks :links="footerLinks" />
          <LanguageSwitcher class="px-3 py-2" />
        </UDashboardSidebar>
      </UDashboardPanel>

      <slot />
    </UDashboardLayout>
  </div>
</template>

<style>
.safe-area-top {
  padding-top: env(safe-area-inset-top);
}

.helper-dashboard :deep(.u-dashboard-navbar-toggle) {
  margin-left: -0.5rem;
}
</style>
